import { Page } from "@/components/layout";
import { TextAgentPage } from "./TextAgentPage";
import { VoiceAgentPage } from "./VoiceAgentPage";
import { useState } from "react";
import { Mic } from "lucide-react";
import { AIMentorPageHeader } from "./components/AIMentorPageHeader";
import { ChatEntry } from "@/types/chat";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { deriveWorkspacePersona, isMentorEntryBlockedForPersona } from "@/config/workspacePersona";
import { isChatEntry, voiceAgentIdsMap } from "@/config/chat.config";
import { cn } from "@/lib/utils";

export const MentorChatPanels = ({
  chatEntry,
  hubLayout = false,
  hubHero = false,
  hubGreetingLine,
}: {
  chatEntry: ChatEntry;
  hubLayout?: boolean;
  /** Show the hub "New Chat" hero (centered greeting/input/chips) when the thread is empty. */
  hubHero?: boolean;
  hubGreetingLine?: string;
}) => {
  const [isVoiceAgent, setIsVoiceAgent] = useState(false);
  const isVoiceModeAvailable = !!voiceAgentIdsMap[chatEntry];

  return (
    <div
      className={cn(
        "flex h-full flex-1 min-h-0",
        hubLayout && !isVoiceAgent && !hubHero ? "flex-row gap-2 md:gap-3 items-stretch" : "flex-col"
      )}
    >
      {isVoiceAgent ? (
        <div className="flex flex-1 min-h-0 min-w-0 flex-col">
          <VoiceAgentPage toggleInputMode={() => setIsVoiceAgent(false)} chatEntry={chatEntry} />
        </div>
      ) : (
        <>
          <div className="flex flex-1 min-h-0 min-w-0 flex-col">
            <TextAgentPage
              toggleInputMode={() => setIsVoiceAgent(true)}
              chatEntry={chatEntry}
              isVoiceModeAvailable={isVoiceModeAvailable}
              hubLayout={hubLayout}
              suppressInlineVoice={hubLayout && !hubHero}
              hubHero={hubHero}
              hubGreetingLine={hubGreetingLine}
            />
          </div>
          {hubLayout && !hubHero && isVoiceModeAvailable && (
            <div className="flex flex-col justify-end shrink-0 pb-2 md:pb-3">
              <button
                type="button"
                onClick={() => setIsVoiceAgent(true)}
                aria-label="Switch to voice mode"
                title="Voice"
                className={cn(
                  "flex flex-col items-center gap-1 rounded-2xl border-2 border-[#C58C10]/70 bg-white/95 px-2.5 py-3",
                  "text-bodhi-teal shadow-sm hover:bg-amber-50/90 transition-colors min-h-[120px] justify-center"
                )}
              >
                <Mic className="w-6 h-6" aria-hidden />
                <span className="text-[10px] font-medium font-inter text-stone-600 leading-tight text-center max-w-[4.5rem]">
                  Voice
                </span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const AIMentorPage = () => {
  const location = useLocation();
  const { userData } = useAuth();
  const raw = location.state?.entry;
  const entry: ChatEntry = isChatEntry(raw) ? raw : "ai_mentor";
  const persona = deriveWorkspacePersona(userData);

  if (isMentorEntryBlockedForPersona(persona, entry)) {
    return <Navigate to="/" replace state={{ entry: "ai_mentor" }} />;
  }

  return (
    <Page.Container key={entry}>
      <Page.Header className="gap-0 pt-2 pb-2 md:gap-0 md:pt-3 md:pb-3">
        <AIMentorPageHeader />
      </Page.Header>

      <Page.Content
        hideFooter
        className="mt-0 flex min-h-0 flex-col flex-1 space-y-0 p-2 pt-1 md:mt-0 md:p-4 md:pt-2"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <MentorChatPanels chatEntry={entry} />
        </div>
      </Page.Content>
    </Page.Container>
  );
};
